/**
 * CORE-936 — a seek target that is safe to write into `HTMLMediaElement.currentTime`.
 *
 * The web seek bar used to compute `ratio * video.duration` and assign the
 * result straight away. `video.duration` is not always a usable number:
 *
 *   - before metadata arrives it is `NaN`;
 *   - for an HLS playlist without a declared length (live/event), and for a
 *     file served without `Content-Length` / byte-range support, the browser
 *     reports `Infinity`.
 *
 * `Infinity` slipped through the old `if (!duration)` guard, so the drag wrote
 * `Infinity` (or `NaN`, when the ratio was 0) and the setter threw
 * `TypeError: … The provided double value is non-finite` on every mouse move —
 * ten unhandled exceptions in 45 seconds in Sentry REACT-NATIVE-3B, with the
 * video never seeking at all.
 *
 * When `duration` is unusable the element still knows how much of the stream it
 * can seek through: `video.seekable`. Using its last range end keeps the seek
 * bar working on those streams instead of merely silencing the crash.
 *
 * Returns `null` when no finite target can be computed — callers must skip the
 * write rather than fall back to a number.
 */

/** Last seekable position of a media element, or `null` if there is none. */
export const readSeekableEnd = (
    seekable: { length: number; end: (index: number) => number } | null | undefined,
): number | null => {
    if (!seekable || seekable.length < 1) return null;
    try {
        const end = seekable.end(seekable.length - 1);
        return Number.isFinite(end) && end > 0 ? end : null;
    } catch {
        // `end()` throws InvalidStateError if the ranges changed under us.
        return null;
    }
};

/**
 * CORE-1121 — whether the element reports ANY seekable range, even an unbounded
 * one. {@link readSeekableEnd} deliberately rejects an `Infinity` end because it
 * can't be a scale — but an `Infinity` end is precisely Chromium's signal that
 * byte-range seeking works while the total length is still unknown. That is the
 * state Yandex.Browser measured on the prod / RU contour: `readyState` 4,
 * `duration` Infinity, `seekable` [0, ∞), and every click a no-op. Callers use
 * this to tell "cannot seek at all" apart from "can seek but has no length yet".
 */
export const hasSeekableRange = (
    seekable: { length: number; end: (index: number) => number } | null | undefined,
): boolean => {
    if (!seekable || seekable.length < 1) return false;
    try {
        return seekable.end(seekable.length - 1) > 0;
    } catch {
        // `end()` throws InvalidStateError if the ranges changed under us.
        return false;
    }
};

/** Options that let a caller scale by a length the element itself never learned. */
export interface SeekScaleOptions {
    /**
     * CORE-1121 — the practice's real length in seconds, from the API record
     * (`durationSeconds`, falling back to `durationMinutes * 60`). Used only as a
     * last resort, and only when {@link SeekScaleOptions.seekablePresent} says the
     * element can actually move the playhead.
     */
    knownDuration?: number | null;
    /** Result of {@link hasSeekableRange} for the same element. */
    seekablePresent?: boolean;
}

/**
 * Duration to scale the drag ratio by: the element's own duration when it is a
 * finite positive number, otherwise the end of the seekable window, otherwise
 * (CORE-1121) the length carried by the practice record — but only when the
 * element reports it can seek at all.
 */
export const resolveSeekDuration = (
    duration: number | null | undefined,
    seekableEnd: number | null,
    opts?: SeekScaleOptions,
): number | null => {
    if (typeof duration === 'number' && Number.isFinite(duration) && duration > 0) return duration;
    if (seekableEnd !== null && Number.isFinite(seekableEnd) && seekableEnd > 0) return seekableEnd;
    // CORE-1121 — neither the element's duration nor its seekable end gives a
    // finite scale, yet `seekable` is [0, ∞): byte ranges work, the element just
    // never learned a length. Scale by the length the API already knows so a
    // click maps to a real time and the browser seeks by byte-range. Attempt 1
    // stopped at making this dead bar honest; this is the seek it left undone.
    //
    // Gated on `seekablePresent` on purpose: on a genuinely non-seekable stream
    // the known length would only paint a working-looking bar that silently
    // ignores clicks — the exact original bug — so there we stay `null`.
    const known = opts?.knownDuration;
    if (opts?.seekablePresent && typeof known === 'number' && Number.isFinite(known) && known > 0) {
        return known;
    }
    return null;
};

/**
 * CORE-1121 — whether the seek bar can move the playhead at all. When neither a
 * finite duration nor a seekable window is known, {@link computeSeekTarget}
 * returns `null` for every click, so the bar looks operable but silently does
 * nothing — the exact "ползунок не работает" report from Yandex.Browser on
 * prod, where a practice master without faststart / working byte ranges never
 * yields a duration and `seekable` stays empty. The UI reads this to render the
 * bar disabled instead of operable.
 */
export const isSeekBarInteractive = (
    duration: number | null | undefined,
    seekableEnd: number | null,
    opts?: SeekScaleOptions,
): boolean => resolveSeekDuration(duration, seekableEnd, opts) !== null;

export const computeSeekTarget = (params: {
    /** Pointer position, page coordinates. */
    clientX: number;
    /** Seek bar bounding box, page coordinates. */
    rectLeft: number;
    rectWidth: number;
    /** `video.duration` as read by the caller. */
    duration: number | null | undefined;
    /** Result of {@link readSeekableEnd} for the same element. */
    seekableEnd: number | null;
} & SeekScaleOptions): number | null => {
    const { clientX, rectLeft, rectWidth, duration, seekableEnd, knownDuration, seekablePresent } = params;
    // A zero-width bar makes the ratio 0/0 = NaN, which is exactly what the
    // setter rejects — there is no meaningful target to compute either.
    if (!Number.isFinite(rectWidth) || rectWidth <= 0) return null;
    const seekDuration = resolveSeekDuration(duration, seekableEnd, { knownDuration, seekablePresent });
    if (seekDuration === null) return null;
    const ratio = Math.max(0, Math.min(1, (clientX - rectLeft) / rectWidth));
    if (!Number.isFinite(ratio)) return null;
    const target = ratio * seekDuration;
    return Number.isFinite(target) ? target : null;
};
